import { Module, Mutation, Action } from "vuex-class-modules"
import { Modules } from "../modules"
import { store } from "../store"

import { Endpoint } from "@/services/endpoints"
import { UpdateObjectValue } from "@/models/common"
import {
  Account,
  AccountContactInformation,
  AccountPictureUpdatePayload,
  AccountContactInformationPayload,
  AccountSetupPayload,
  AccountSetupProgress,
  DemoAccount,
  AccountsAnalytics,
  SubscriptionUpgrade,
  UserCounts
} from "@/models/Account"
import { SearchResult } from "@/models/Search"
import { FilterAccount, FilterDemoAccount } from "@/models/Filter/FilterAccount"

import { ServerResponse } from "@/models/ServerResponse"
import { Role, User } from "@/models/User"
import { BaseModule } from "@/models/BaseModule"
import { apiService } from "@/services/api.service"
import { AlertType, AlertContentItem } from "@/models/Alert"
import { $t } from "@/plugins/i18n"
import { AlertActions, alertModule } from "./alert.module"
import { forceUpdateToken } from "@/plugins/keycloak"
import { parsingServerResponseContent } from "@/utils/helpers"
import { PaymentRequest, TypeOfSubscriptionFeatures, PaymentSubscription, StripePrice, StripeProduct, BillingInterval } from "@/models/Stripe"

export enum AccountActions {
  GET_ACCOUNTS = "GET_ACCOUNTS",
  GET_ACCOUNTS_RESPONSE = "GET_ACCOUNTS_RESPONSE",
  GET_ACCOUNT = "GET_ACCOUNT",
  GET_ACCOUNT_ID = "GET_ACCOUNT_ID",
  SET_ACCOUNT_CREATING_STATUS = "SET_ACCOUNT_CREATING_STATUS",
  SET_ACCOUNT_ID = "SET_ACCOUNT_ID",
  UPDATE_ACCOUNT = "UPDATE_ACCOUNT",
  POST_ACCOUNT = "POST_ACCOUNT",
  GET_CURRENT_ACCOUNT_ID = "GET_CURRENT_ACCOUNT_ID",
  GET_CURRENT_ACCOUNT_NAME = "GET_CURRENT_ACCOUNT_NAME",
  GET_USER_CURRENT_ACCOUNTS = "GET_USER_CURRENT_ACCOUNTS",
  GET_ACCOUNT_SUPERADMIN = "GET_ACCOUNT_SUPERADMIN",
  ACCOUNT_PICTURE_UPDATE = "ACCOUNT_PICTURE_UPDATE",
  ACCOUNT_PICTURE_DELETE = "ACCOUNT_PICTURE_DELETE",
  UPDATE_ACCOUNT_CONTACT_INFO = "UPDATE_ACCOUNT_CONTACT_INFO",
  UPDATE_ACCOUNT_VALUE = "UPDATE_ACCOUNT_VALUE",
  CHECK_ACCOUNT_NAME = "CHECK_ACCOUNT_NAME",
  GET_ACCOUNT_ADMIN = "GET_ACCOUNT_ADMIN",
  UPDATE_ACCOUNT_SUPERADMIN = "UPDATE_ACCOUNT_SUPERADMIN",
  UPDATE_ACCOUNT_AVAILABILITY = "UPDATE_ACCOUNT_AVAILABILITY",
  UPDATE_TEST_MARK = "UPDATE_TEST_MARK",
  UPDATE_ACCOUNT_MCP_ENABLED = "UPDATE_ACCOUNT_MCP_ENABLED",
  GET_ACCOUNT_SETUP_PROGRESS = "GET_ACCOUNT_SETUP_PROGRESS",
  SETUP_ACCOUNT = "SETUP_ACCOUNT",
  UPDATE_ACCOUNT_SETUP_STEP = "UPDATE_ACCOUNT_SETUP_STEP",
  COMPLETE_ACCOUNT_SETUP = "COMPLETE_ACCOUNT_SETUP",
  GET_DEMO_REQUESTS = "GET_DEMO_REQUESTS",
  GET_ACCOUNTS_ANALYTICS = "GET_ACCOUNTS_ANALYTICS",
  DELETE_DEMO_REQUESTS = "DELETE_DEMO_REQUESTS",
  STRIPE_GET_PRODUCTS = "STRIPE_GET_PRODUCTS",
  STRIPE_GET_PRICES = "STRIPE_GET_PRICES",
  STRIPE_SUBSCRIBE = "STRIPE_SUBSCRIBE",
  SERVER_ERROR = "SERVER_ERROR",
  PAYMENT_SUBSCRIPTION_START = "PAYMENT_SUBSCRIPTION_START",
  CURRENT_STRIPE_PRICE = "CURRENT_STRIPE_PRICE",
  UPGRADE_SUBSCRIPTION = "UPGRADE_SUBSCRIPTION",
  PAYMENT_SUBSCRIPTION_START_TRIAL = "PAYMENT_SUBSCRIPTION_START_TRIAL",
  PAYMENT_SUBSCRIPTION_CONFIRM = "PAYMENT_SUBSCRIPTION_CONFIRM",
  SELECTED_SUBSCRIPTION_FEATURE = "SELECTED_SUBSCRIPTION_FEATURE",
  ACCOUNT_PAYMENT_ENABLE = "ACCOUNT_PAYMENT_ENABLE",
  GET_PAYMENT_LINK = "GET_PAYMENT_LINK",
  SET_STRIPE_CUSTOMER_ID = "SET_STRIPE_CUSTOMER_ID",
  GET_CURRENT_ACCOUNT_HAS_PAYMENT_VERIFIED = "GET_CURRENT_ACCOUNT_HAS_PAYMENT_VERIFIED",
  SET_CURRENT_ACCOUNT_PAYMENT_VERIFIED = "SET_CURRENT_ACCOUNT_PAYMENT_VERIFIED",
  GET_SELECTED_INTERVAL = "GET_SELECTED_INTERVAL",
  SET_SELECTED_INTERVAL = "SET_SELECTED_INTERVAL",
  PROMO_CODE_CHECK = "PROMO_CODE_CHECK",
  GET_USER_COUNTS = "GET_USER_COUNTS",
  HAS_AI_CONFIG = "HAS_AI_CONFIG"
}

@Module
class AccountModule extends BaseModule {
  accounts: Account[] = []
  serverResponse: ServerResponse<Account>
  account: Account | null = null
  accountId = ""
  accountUuids: string[]
  accountOverview: User
  accountCreating = false
  demoAccounts: ServerResponse<DemoAccount> = new ServerResponse<DemoAccount>()
  accountAnalytics: AccountsAnalytics = new AccountsAnalytics()
  stripeProducts: StripeProduct[] = []
  stripePrices: StripePrice[] = []
  hasAiConfig = false
  selectedSubscriptionFeature: TypeOfSubscriptionFeatures | null = null
  selectedInterval: BillingInterval = "month"
  setupProgress: AccountSetupProgress | null = null
  setupProgressAccountId = ""

  get [`get/${AccountActions.GET_CURRENT_ACCOUNT_ID}`]() {
    return this.getCurrentAccountId()
  }

  get [`get/${AccountActions.GET_CURRENT_ACCOUNT_NAME}`]() {
    return this.getCurrentAccountName()
  }

  get [`get/${AccountActions.GET_CURRENT_ACCOUNT_HAS_PAYMENT_VERIFIED}`]() {
    return this.getCurrentAccountHasPaymentVerified()
  }

  get [`get/${AccountActions.GET_ACCOUNT_ID}`]() {
    return this.accountId
  }

  get [`get/${AccountActions.GET_ACCOUNT}`]() {
    return this.account
  }

  get [`get/${AccountActions.GET_ACCOUNTS}`]() {
    return this.accounts
  }

  get [`get/${AccountActions.GET_ACCOUNTS_RESPONSE}`]() {
    return this.serverResponse
  }

  get [`get/${AccountActions.GET_USER_CURRENT_ACCOUNTS}`]() {
    return this.accountUuids
  }

  get [`get/${AccountActions.GET_DEMO_REQUESTS}`]() {
    return this.demoAccounts
  }

  get [`get/${AccountActions.GET_ACCOUNTS_ANALYTICS}`]() {
    return this.accountAnalytics
  }

  get [`get/${AccountActions.STRIPE_GET_PRODUCTS}`]() {
    return this.stripeProducts
  }

  get [`get/${AccountActions.STRIPE_GET_PRICES}`]() {
    return this.stripePrices
  }

  get [`get/${AccountActions.CURRENT_STRIPE_PRICE}`]() {
    return this.stripePrices.find((price) => price.productId === this.account?.paymentSubscription?.productId)
  }

  get [`get/${AccountActions.SELECTED_SUBSCRIPTION_FEATURE}`]() {
    return this.selectedSubscriptionFeature
  }

  get [`get/${AccountActions.GET_SELECTED_INTERVAL}`]() {
    return this.selectedInterval
  }

  get [`get/${AccountActions.HAS_AI_CONFIG}`]() {
    return this.hasAiConfig
  }

  @Mutation
  setAccountId(id: string) {
    this.accountId = id
  }

  @Mutation
  setAccountCreating(val) {
    this.accountCreating = val
  }

  @Mutation
  setAccounts(accounts: Account[]) {
    this.accounts = accounts
  }

  @Mutation
  addAccounts(accounts: Account[]) {
    this.accounts = [...this.accounts, ...accounts]
  }

  @Mutation
  setAccountsResponse(response: ServerResponse<Account>) {
    this.serverResponse = response
  }

  @Mutation
  setAccount(account: Account) {
    this.account = account
    this.accountId = account.id
  }

  @Mutation
  setAccountValue(payload) {
    if (!this.account) return
    Object.assign(this.account, { [payload["id"]]: payload["value"] })
  }

  @Mutation
  setSetupProgress({ accountId, setupProgress }: { accountId: string, setupProgress: AccountSetupProgress }) {
    this.setupProgressAccountId = accountId
    this.setupProgress = setupProgress
  }

  @Mutation
  setAccountOverview(accountOverview: User) {
    this.accountOverview = accountOverview
  }

  @Mutation
  setUserCurrentAccounts(uuids: string[]) {
    this.accountUuids = uuids
  }

  @Mutation
  setAccountAnalytics(accountAnalytics: AccountsAnalytics) {
    this.accountAnalytics = accountAnalytics
  }

  @Mutation
  removeDemoRequest(id: string) {
    this.demoAccounts.content = this.demoAccounts.content.filter((account) => account.id !== id)
  }

  @Mutation
  setStripePrices(stripePrices: StripePrice[]) {
    this.stripePrices = stripePrices
  }

  @Mutation
  setDemoAccounts(demoAccounts: ServerResponse<DemoAccount>) {
    const content = parsingServerResponseContent({
      originalContent: this.demoAccounts.content,
      serverResponseContent: demoAccounts.content,
      numberOfPage: demoAccounts.pageable.pageNumber,
    })
    this.demoAccounts = { ...demoAccounts, content }
  }

  @Mutation
  setAccountPictureUrl({ accountId, pictureUrl }: { accountId: string, pictureUrl: string }) {
    if (this.account && this.account.id === accountId) {
      this.account.picture = this.account.picture || {}
      this.account.picture.pictureUrl = pictureUrl
    }
    const account = this.accounts.find(t => t.id === accountId)
    if (account) {
      account.picture = account.picture || {}
      account.picture.pictureUrl = pictureUrl
    }
  }

  @Mutation
  setStripePrises(stripePrices: StripePrice[]){
    this.stripePrices = stripePrices
  }

  @Mutation
  setStripeProducts(stripeProducts: StripeProduct[]){
    this.stripeProducts = stripeProducts
  }

  @Mutation
  setAccountPaymentVerified(){
    if (this.accountOverview && this.accountOverview?.accounts?.length) {
      this.accountOverview.accounts[0].hasPaymentMethod = true;
    }
    if (this.account && this.account?.paymentSubscription) {
      this.account.hasPaymentMethod = true;
    }
  }

  @Mutation
  setSelectedSubscriptionFeature(value: TypeOfSubscriptionFeatures) {
    this.selectedSubscriptionFeature = value
  }

  @Mutation
  setSelectedInterval(period: BillingInterval) {
    this.selectedInterval = period
  }

  @Mutation
  setHasAiConfig(value: boolean) {
    this.hasAiConfig = value
  }

  @Action
  async [AccountActions.DELETE_DEMO_REQUESTS]({ id } : { id: string }) {
    const data = await apiService.delete<ServerResponse<Account>>(Endpoint.DELETE_DEMO_REQUESTS(id))
    return this.handleResponse<ServerResponse<Account>>(data, () => {
      const alertContent: AlertContentItem[] = [
        {
          text: `Demo Request ${$t("alert.deleted")}`,
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
      this.removeDemoRequest(id)
    })
  }

  @Action
  async [AccountActions.GET_ACCOUNTS](params: FilterAccount) {
    const filter = FilterAccount.create(params)
    const data = await apiService.get<ServerResponse<Account>>(Endpoint.GET_ACCOUNTS(), filter.getJsonObj())
    return this.handleResponse<ServerResponse<Account>>(data, (data) => {
      this.setAccountsResponse(data)
      const accounts = data.content.map((account: Account) => Account.create(account))
      if (filter.page === 1) {
        this.setAccounts(accounts)
      } else {
        this.addAccounts(accounts)
      }
      return data
    })
  }

  @Action
  async [AccountActions.GET_DEMO_REQUESTS](filter: FilterDemoAccount) {
    const data = await apiService.get<ServerResponse<DemoAccount>>(Endpoint.GET_DEMO_REQUESTS(), filter.getJsonObj())
    return this.handleResponse<ServerResponse<DemoAccount>>(data, (data) => {
      const content = data.content.map((account) => new DemoAccount(account))
      data.content = content
      this.setDemoAccounts(data)
      return content
    })
  }

  @Action
  async [AccountActions.GET_ACCOUNTS_ANALYTICS]() {
    const data = await apiService.get<AccountsAnalytics>(Endpoint.GET_ACCOUNTS_ANALYTICS())
    return this.handleResponse<AccountsAnalytics>(data, (data) => {
      const analytics = new AccountsAnalytics(data)
      this.setAccountAnalytics(analytics)
      return analytics
    })
  }

  @Action
  async [AccountActions.SET_ACCOUNT_CREATING_STATUS](val) {
    this.setAccountCreating(val)
  }

  @Action
  async [AccountActions.GET_USER_CURRENT_ACCOUNTS]() {
    const data = await apiService.get<string[]>(Endpoint.USER_CURRENT_ACCOUNTS())
    return this.handleResponse<string[]>(data, (data) => {
      this.setUserCurrentAccounts(data)
      return data ?? []
    })
  }

  @Action
  async [AccountActions.GET_ACCOUNT](id: string) {
    const data = await apiService.get<Account>(Endpoint.GET_ACCOUNT(id))
    return this.handleResponse<Account>(data, (data) => {
      const acc = Account.create(data)
      this.setAccount(acc)
      return acc
    })
  }

  @Action
  async [AccountActions.GET_ACCOUNT_ADMIN](id: string) {
    const data = await apiService.get<Account>(Endpoint.GET_ACCOUNT_ADMIN(id))
    return this.handleResponse<Account>(data, (data) => {
      this.setAccount(Account.create(data))
    })
  }

  @Action
  async [AccountActions.GET_ACCOUNT_SUPERADMIN](id: string) {
    const data = await apiService.get<Account>(Endpoint.GET_ACCOUNT_SUPERADMIN(id))
    return this.handleResponse<Account>(data, (data) => {
      this.setAccount(Account.create(data))
    })
  }

  @Action
  async [AccountActions.UPDATE_ACCOUNT_SUPERADMIN]({ account, withoutUpdate }: { account: Account, withoutUpdate?: boolean }) {
    const data = await apiService.put<Account>(Endpoint.GET_ACCOUNT_SUPERADMIN(account.id), account.getJsonObj())
    return this.handleResponse<Account>(data, (data) => {
      const createAccount = Account.create(data)
      if(!withoutUpdate) {
        this.setAccount(createAccount)
      }
      const alertContent: AlertContentItem[] = [
        {
          text: "Account Settings ",
          type: "bold",
        },
        {
          text: "Saved",
          type: "regular",
        },
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
      return createAccount
    })
  }

  @Action
  async [AccountActions.UPDATE_ACCOUNT]({ account, withoutUpdate }: { account: Account, withoutUpdate?: boolean }) {
    const data = await apiService.patch<Account>(Endpoint.UPDATE_ACCOUNT(account.id), account.getJsonObj())
    return this.handleResponse<Account>(data, () => {
      const createAccount = Account.create(account)
      if(!withoutUpdate) {
        this.setAccount(createAccount)
      }
      const alertContent: AlertContentItem[] = [
        {
          text: "Account Settings ",
          type: "bold",
        },
        {
          text: "Saved",
          type: "regular",
        },
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
      return createAccount
    })
  }

  @Action
  async [AccountActions.POST_ACCOUNT](payload: { name: string, ownerTeamRole: Role, contactInformation: AccountContactInformation, promoCode?: string }) {
    this.setError(null)

    const data = await apiService.post<Account>(Endpoint.POST_ACCOUNT(), payload)
    return this.handleResponse<Account>(data, async (data) => {
      await forceUpdateToken()
      const account = Account.create(data)
      this.setAccount(account)

      return account
    })
  }

  @Action
  async [AccountActions.ACCOUNT_PICTURE_UPDATE](payload: AccountPictureUpdatePayload) {
    const file = new FormData()
    file.append("file", payload.file)
    const data = await apiService.post<string>(Endpoint.ACCOUNT_PICTURE(payload.accountId), file)
    return this.handleResponse<string>(data, (data) => {
      const alertContent: AlertContentItem[] = [
        {
          text: "Account Picture",
          type: "bold",
        },
        {
          text: " updated",
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
      this.setAccountPictureUrl({
        accountId: payload.accountId,
        pictureUrl: data,
      })
    }, true, (error) => {
      const alertContent: AlertContentItem[] = [
        {
          text: error.response.data?.message || "Error while uploading picture",
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.ERROR,
        theme: "toast",
        content: alertContent,
      })
    })
  }

  @Action
  async [AccountActions.ACCOUNT_PICTURE_DELETE](accountId: string) {
    const data = await apiService.delete<string>(Endpoint.ACCOUNT_PICTURE(accountId))
    return this.handleResponse<string>(data, () => {
      const alertContent: AlertContentItem[] = [
        {
          text: "Account Picture",
          type: "bold",
        },
        {
          text: " deleted",
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
      this.setAccountPictureUrl({
        accountId: accountId,
        pictureUrl: "",
      })
    })
  }

  @Action
  async [AccountActions.UPDATE_ACCOUNT_CONTACT_INFO](payload: AccountContactInformationPayload) {
    const endpointName = payload.isSuperAdmin ? "ACCOUNT_CONTACT_INFO_SUPER_ADMIN" : "ACCOUNT_CONTACT_INFO"
    const data = await apiService.put<Account>(Endpoint[endpointName](payload.accountId), payload.information)
    return this.handleResponse<Account>(data, () => {
      const alertContent: AlertContentItem[] = [
        {
          text: "Account Settings",
          type: "bold",
        },
        {
          text: " saved",
          type: "regular",
        },
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
    })
  }

  @Action
  [AccountActions.SET_ACCOUNT_ID](id: string) {
    this.setAccountId(id)
  }

  @Action
  [AccountActions.UPDATE_ACCOUNT_VALUE](payload: UpdateObjectValue<Account>) {
    this.setAccountValue(payload)
  }

  @Action
  async [AccountActions.CHECK_ACCOUNT_NAME](name: string) {
    const data = await apiService.get<boolean>(Endpoint.ACCOUNT_CHECK(), {
      name,
    })
    return this.handleResponse<boolean>(data, () => data ? [{
      id: "",
      resourceType: "ACCOUNT",
      textPreview: name,
      type: "TIP",
    }] as SearchResult[] : [])
  }

  @Action
  async [AccountActions.UPDATE_ACCOUNT_AVAILABILITY](payload: { accountId: string, name: string, enabled: boolean }) {
    const data = await apiService.patch<boolean>(Endpoint.ACCOUNT_ENABLE(payload.accountId), null, {
      params: { enabled: payload.enabled },
    })

    return this.handleResponse<boolean>(data, () => {
      this.setAccountValue({ id: "enabled", value: payload.enabled })

      const alertContent: AlertContentItem[] = [
        {
          text: "Account",
          type: "regular",
        },
        {
          text: ` ${payload.name} `,
          type: "bold",
        },
        {
          text: data ? "was enabled" : "was disabled",
          type: "regular",
        },
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
    })
  }

  @Action
  async [AccountActions.ACCOUNT_PAYMENT_ENABLE](payload: { accountId: string, name: string, enabled: boolean }) {
    const data = await apiService.patch<boolean>(Endpoint.ACCOUNT_PAYMENT_ENABLE(payload.accountId), null, {
      params: { enabled: payload.enabled },
    })

    return this.handleResponse<boolean>(data, () => {
      this.setAccountValue({ id: "enabledBySubscription", value: payload.enabled })

      const alertContent: AlertContentItem[] = [
        {
          text: "Account",
          type: "regular",
        },
        {
          text: ` ${payload.name} `,
          type: "bold",
        },
        {
          text: data ? "was enabled" : "was disabled",
          type: "regular",
        },
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
    })
  }

  @Action
  async [AccountActions.UPDATE_TEST_MARK ](payload: { accountId: string, name: string, test: boolean }) {
    const data = await apiService.patch<boolean>(Endpoint.ACCOUNT_TEST(payload.accountId), null, {
      params: { testAccount: payload.test },
    })

    return this.handleResponse<boolean>(data, () => {
      this.setAccountValue({ id: "test", value: payload.test })

      const alertContent: AlertContentItem[] = [
        {
          text: "Account",
          type: "regular",
        },
        {
          text: ` ${payload.name} `,
          type: "bold",
        },
        {
          text: data ? "successfully marked as Test" : "successfully unmarked as Test",
          type: "regular",
        },
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
    })
  }

  @Action
  async [AccountActions.UPDATE_ACCOUNT_MCP_ENABLED](payload: { accountId: string, enabled: boolean }) {
    const data = await apiService.patch<boolean>(Endpoint.ACCOUNT_MCP_ENABLE(payload.accountId), null, {
      params: { enabled: payload.enabled },
    })

    return this.handleResponse<boolean>(data, (mcpEnabled) => {
      this.setAccountValue({ id: "settings", value: { ...this.account?.settings, mcpEnabled } })

      const alertContent: AlertContentItem[] = [
        {
          text: "MCP Server ",
          type: "bold",
        },
        {
          text: mcpEnabled ? "enabled" : "disabled",
          type: "regular",
        },
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
    })
  }

  /**
   * Loads the sign-up progress of an account once per session and caches it by account id.
   *
   * Reads `GET /account/{id}` instead of `GET_ACCOUNT` so the router check does not replace the current account.
   */
  @Action
  async [AccountActions.GET_ACCOUNT_SETUP_PROGRESS](accountId: string) {
    if (this.setupProgress && this.setupProgressAccountId === accountId) return this.setupProgress
    const data = await apiService.get<{ setupProgress: AccountSetupProgress }>(Endpoint.ACCOUNT_BY_ID(accountId))
    return this.handleResponse<{ setupProgress: AccountSetupProgress }>(data, ({ setupProgress }) => {
      this.setSetupProgress({ accountId, setupProgress })
      return setupProgress
    }, true)
  }

  /**
   * Saves the company name (wizard step 1) or the owner's role (step 2) during sign-up.
   */
  @Action
  async [AccountActions.SETUP_ACCOUNT]({ accountId, ...setup }: AccountSetupPayload) {
    const data = await apiService.patch<void>(Endpoint.ACCOUNT_SETUP(accountId), setup)
    return this.handleResponse<void>(data, () => {
      if (!setup.name) return
      this.setAccountValue({ id: "name", value: setup.name })
      this.setAccountValue({ id: "urlName", value: setup.urlName })
    })
  }

  /**
   * Stores the sign-up wizard step the user has reached, so a refresh or a later login resumes on it.
   */
  @Action
  async [AccountActions.UPDATE_ACCOUNT_SETUP_STEP]({ accountId, step }: { accountId: string, step: string }) {
    const data = await apiService.patch<void>(Endpoint.ACCOUNT_SETUP_STEP(accountId), null, {
      params: { step },
    })
    return this.handleResponse<void>(data, () => {
      this.setSetupProgress({ accountId, setupProgress: { setupCompleted: false, lastStep: step } })
    })
  }

  /**
   * Marks the sign-up as finished, after which the router stops sending the owner back to the wizard.
   */
  @Action
  async [AccountActions.COMPLETE_ACCOUNT_SETUP](accountId: string) {
    const data = await apiService.patch<void>(Endpoint.ACCOUNT_COMPLETE_SETUP(accountId))
    return this.handleResponse<void>(data, () => {
      this.setSetupProgress({ accountId, setupProgress: { setupCompleted: true } })
    })
  }

  @Action
  async [AccountActions.SELECTED_SUBSCRIPTION_FEATURE](value: TypeOfSubscriptionFeatures) {
    return this.setSelectedSubscriptionFeature(value)
  }

  @Action
  async [AccountActions.UPGRADE_SUBSCRIPTION]({ params }: { params: SubscriptionUpgrade }) {
    const data = await apiService.put<StripePrice>(Endpoint.UPGRADE_SUBSCRIPTION(this.accountId), params)
    return this.handleResponse<StripePrice>(data, (data) => {
      this.setAccountValue({
        id: "paymentSubscription",
        value: {
          productId: data.productId,
          status: "ACTIVE",
        }
      })
      this.setAccountValue({
        id: "hasSubscriptionAccess",
        value: true
      })
      const alertContent: AlertContentItem[] = [
        {
          text: "Congratulations! You're now subscribed to the ",
          type: "regular",
        },
        {
          text: `${data.productName} plan!`,
          type: "bold",
        }
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
    })
  }

  @Action
  async [AccountActions.STRIPE_GET_PRICES]() {
    const data = await apiService.get<StripePrice[]>(Endpoint.STRIPE_GET_PRICES())
    return this.handleResponse<StripePrice[]>(data, (data)=>{
      this.setStripePrises(data)
    })
  }

  @Action
  async [AccountActions.GET_PAYMENT_LINK](accountId) {
    const data = await apiService.get<string>(Endpoint.PAYMENT_LINK(accountId))
    return this.handleResponse<string>(data, (data)=>{
      return data
    })
  }

  @Action
  async [AccountActions.SET_STRIPE_CUSTOMER_ID]({ accountId, customerId }: { accountId: string, customerId: string }) {
    const data = await apiService.patch<Account>(Endpoint.SET_STRIPE_CUSTOMER_ID(accountId, customerId))
    return this.handleResponse<Account>(data, (data) => {
      const account = Account.create(data)
      this.setAccount(account)
      const alertContent: AlertContentItem[] = [
        {
          text: "Customer id ",
          type: "bold",
        },
        {
          text: "updated",
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.SUCCESS,
        theme: "toast",
        content: alertContent,
      })
      return account
    })
  }

  @Action
  async [AccountActions.STRIPE_GET_PRODUCTS]() {

    const data = await apiService.get<StripeProduct[]>(Endpoint.STRIPE_GET_PRODUCTS())
    return this.handleResponse<StripeProduct[]>(data, (data) =>{
      this.setStripeProducts(data)
    })
  }

  @Action
  async [AccountActions.STRIPE_SUBSCRIBE](payload: PaymentRequest) {
    const data = await apiService.post<void>(Endpoint.STRIPE_SUBSCRIBE(), payload)
    return this.handleResponse<void>(data)
  }

  getCurrentAccountId() {
    return this.accountOverview?.accounts?.[0]?.id ?? this.accountId
  }

  getCurrentAccountName() {
    return this.accountOverview?.accounts?.[0]?.name ?? this.account?.name
  }

  getCurrentAccountHasPaymentVerified() {
    const selectedAccount = this.accountOverview?.accounts?.[0] ?? this.account
    return selectedAccount?.hasPaymentMethod ?? null
  }

  @Action
  async [AccountActions.SERVER_ERROR]() {
     await apiService.get<void>(Endpoint.SERVER_ERROR())
  }

  @Action
  async [AccountActions.PAYMENT_SUBSCRIPTION_START]({ accountId, priceId } : {accountId: string, priceId: string})  {
    const data = await apiService.post<PaymentSubscription>(Endpoint.PAYMENT_SUBSCRIPTION_START(accountId), {},{ params: { priceId } })

    return this.handleResponse<PaymentSubscription>(data, (data) => {
      return data
    })
  }

  @Action
  async [AccountActions.SET_CURRENT_ACCOUNT_PAYMENT_VERIFIED](){
    this.setAccountPaymentVerified()
  }

  @Action
  async [AccountActions.PAYMENT_SUBSCRIPTION_START_TRIAL]({ accountId, priceId } : {accountId: string, priceId: string})  {
    const data = await apiService.post<PaymentSubscription>(Endpoint.PAYMENT_SUBSCRIPTION_START_TRIAL(accountId), {},{ params: { priceId } })

    return this.handleResponse<PaymentSubscription>(data, (data) => {
      return data
    })
  }

  @Action
  async [AccountActions.PROMO_CODE_CHECK](code: string)  {
    const data = await apiService.post<boolean>(Endpoint.PROMO_CODE_CHECK(), { code })

    return this.handleResponse<boolean>(data, (data) => {
      return data.toString()
    })
  }

  @Action
  async [AccountActions.SET_SELECTED_INTERVAL](period: BillingInterval) {
    this.setSelectedInterval(period)
  }

  @Action
  async [AccountActions.GET_USER_COUNTS]({ accountId, teamId }: { accountId: string; teamId?: string }) {
    const data = await apiService.get<UserCounts>(Endpoint.GET_USER_COUNTS(accountId, teamId))
    return this.handleResponse<UserCounts>(data, (data) => {
      return new UserCounts(data)
    })
  }

  @Action
  async [AccountActions.HAS_AI_CONFIG](accountId: string) {
    const data = await apiService.get<boolean>(Endpoint.HAS_AI_CONFIG(accountId))
    return this.handleResponse<boolean>(data, (hasAiConfig) => {
      this.setHasAiConfig(hasAiConfig)
    })
  }
}

export const accountModule = new AccountModule({
  store,
  name: Modules.ACCOUNT,
})
